import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { getPublicChildren, getPublicPage } from '../api/knowledge';
import type { KnowledgePageDto } from '../api/types';
import { MarkdownEditor } from '../components/MarkdownEditor';
import { IconChevron } from '../components/icons';
import '../styles/knowledge.scss';

/**
 * Unauthenticated read of a PUBLIC knowledge page (the /p/:id share link). Any
 * page that does not resolve to PUBLIC returns 404 from the API, which we show
 * as a neutral "not available" screen — no private content or titles leak.
 */
export default function PublicPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pageId } = useParams<{ pageId: string }>();
  const [page, setPage] = useState<KnowledgePageDto | null>(null);
  const [children, setChildren] = useState<KnowledgePageDto[]>([]);
  const [state, setState] = useState<'loading' | 'ok' | 'missing'>('loading');

  useEffect(() => {
    if (!pageId) return;
    let alive = true;
    setState('loading');
    setChildren([]);
    getPublicPage(pageId)
      .then((p) => {
        if (!alive) return;
        setPage(p);
        setState('ok');
        return getPublicChildren(pageId).then((c) => alive && setChildren(c));
      })
      .catch(() => alive && setState('missing'));
    return () => {
      alive = false;
    };
  }, [pageId]);

  if (state === 'loading') {
    return <div className="kn-public-shell"><div className="kn-empty">{t('app.loading')}</div></div>;
  }
  if (state === 'missing' || !page) {
    return (
      <div className="kn-public-shell">
        <div className="kn-public-missing">
          <div className="kn-public-brand">MySecondBrain</div>
          <p>{t('knowledge.publicMissing')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="kn-public-shell">
      <article className="kn-public">
        <div className="kn-public-topbar">MySecondBrain</div>
        <div className="kn-doc-heading">
          <span className="kn-doc-emoji kn-public-emoji">{page.icon || '📄'}</span>
          <h1 className="kn-public-title">{page.title}</h1>
        </div>
        {page.bodyMarkdown && page.bodyMarkdown.trim() && (
          <MarkdownEditor
            value={page.bodyMarkdown}
            editable={false}
            onChange={() => undefined}
            onOpenInternal={(id) => navigate(`/p/${id}`)}
          />
        )}
        {children.length > 0 && (
          <div className="kn-subpages">
            <div className="kn-subpages-head">{t('knowledge.subpages')}</div>
            {children.map((c) => (
              <button
                key={c.id}
                type="button"
                className="kn-subpage"
                onClick={() => navigate(`/p/${c.id}`)}
              >
                <span className="kn-subpage-icon">{c.icon || '📄'}</span>
                <span className="kn-subpage-title">{c.title}</span>
                <IconChevron className="kn-subpage-arrow" />
              </button>
            ))}
          </div>
        )}
      </article>
    </div>
  );
}
